import {
  alpha,
  Box,
    Button,
    Checkbox,
    Collapse,
    Divider,
    IconButton,
    InputBase,
    Menu,
    MenuItem,
    MenuList,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    TextField,
    Toolbar,
    Tooltip,
} from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import EditIcon from '@mui/icons-material/Edit';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import FilterListIcon from "@mui/icons-material/FilterList"

import * as admin from 'firebase-admin';

import moment from 'moment';
import React, { useEffect, useRef, useState, Dispatch } from "react";
import { useDispatch, useSelector } from "react-redux";

import { firebaseUser } from "../firebase";
import { setAccounts } from '../actions/accounts';
import { setAccountInfos } from '../actions/accountInfos';
import { setUserRoles } from '../actions/userRoles';
import { setUserValidities } from '../actions/userValidities';
import { setMissionMetadata } from "../actions/missionMetadata";
import { 
  getAccounts, 
  getUserRoles, 
  getUserValidities, 
  getAccountInfos, 
  updateFirebaseAccountInfo, 
  deleteUserProfile, 
  getUserProfile, 
  getMissionDomain 
} from '../api';
import EditAccountDialog from '../components/EditAccountDialog';
import { AutoUpdateValidityDialog } from '../components/AutoUpdateValidityDialog';
import { EditDemoMissionDialog } from '../components/EditDemoMissionDialog';
import PlaytimeDialog from '../components/PlaytimeDialog';
import ResetPasswordDialog from '../components/ResetPasswordDialog';
import SimpleSnackBar, { SnackBarProp } from "../components/SimpleSnackBar";
import DeleteDialog from "../components/DeleteDialog";
import { AppState } from "../reducers";
import { LoadableAccountData } from '../reducers/accounts';
import { StyledCircularProgress, StyledContainer } from "../StyledComponents";
import { AccountInfo } from '../types/AccountInfo';
import { RoleType, UserRole, isTrialAccount } from "../types/UserRole";
import { UserValidity } from "../types/UserValidity";
import { MissionMetadata, MissionType } from "../types/MissionType";

import { useTranslation } from "react-i18next";

type UserRecord = admin.auth.UserRecord;

export interface AccountData extends UserRecord {
  role?: RoleType | null,
  referralCode?: string,
  is_valid?: boolean | null,
  valid_till?: number | string | null,
  creationTime: string | null,
  lastSignInTime: string | null
}

export default function AccountsPage(props: { setTitle: Dispatch<React.SetStateAction<string>>}) {

  const {t} = useTranslation()

  const dispatch = useDispatch();
  const [isEditAccountDialogOpen, setIsEditAccountDialogOpen] = useState<boolean>(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState<boolean>(false);
  const [isPlaytimeDialogOpen, setIsPlaytimeDialogOpen] = useState<boolean>(false);
  const [isEditDemoMissionDialogOpen, setIsEditDemoMissionDialogOpen] = useState<boolean>(false);
  const [isAutoUpdateValidityDialogOpen, setIsAutoUpdateValidityDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const closeErrorSnackBar = () => {
    setSnackBar({ ...snackBar, "open": false });
  }
  const [snackBar, setSnackBar] = useState<SnackBarProp>({
    open: false,
    onClose: closeErrorSnackBar,
    message: "",
    variant: undefined
  });

  const [selectedAccount, setSelectedAccount] = useState<string>();

  const loggedInUser = useSelector<AppState, firebaseUser | null>(state => state.loggedInUser);

  const loggedUserRole = useSelector<AppState, RoleType | null>(state => state.userRole);

  const accounts = useSelector<AppState, LoadableAccountData>(state => state.accounts);

  const userRoles = useSelector<AppState, UserRole[] | null>(state => state.userRoles);

  const userValidities = useSelector<AppState, UserValidity[] | null>(state => state.userValidities);

  const accountInfos = useSelector<AppState, AccountInfo[] | null>(state => state.accountInfos);

  const [accountDatas, setAccountDatas] = useState<AccountData[]>();

  const missionMetadata = useSelector<AppState, { metadata: MissionMetadata | null; domainMission: { [key: string]: MissionType[] } }>(state => state.missionMetadata);

  useEffect(() => {
    async function setMissionMD() {
      if (!missionMetadata.metadata)
        dispatch(setMissionMetadata(await getMissionDomain()));
    }
    setMissionMD();
    _setAccounts();
    props.setTitle("accounts");
  }, [])

  useEffect(() => {
    _setAccountDatas();
  }, [accounts, userRoles, userValidities, accountInfos])

  const _setAccounts = async () => {
    if (accounts.kind === "notLoaded") {
      dispatch(setAccounts({ kind: "loading" }));
    }
    try {
      let data = await getAccounts() as AccountData[];
      let mapped_data = data.map(x => {
        return {
          ...x,
          creationTime: moment.utc(x.metadata.creationTime).local().format('YYYY-MM-DD HH:mm:ss'),
          lastSignInTime: moment.utc(x.metadata.lastSignInTime).local().format('YYYY-MM-DD HH:mm:ss')
        }
      }) as AccountData[];
      const _userValidities = await getUserValidities();
      dispatch(setUserValidities(_userValidities));
      const _userRoles = await getUserRoles();
      dispatch(setUserRoles(_userRoles));
      const _accountInfos = await getAccountInfos();
      dispatch(setAccountInfos(_accountInfos));
      dispatch(setAccounts({ kind: "loaded", data: mapped_data }));
    } catch (e: any) {
      dispatch(setAccounts({ kind: "error", error: e }));
    }
  }

  const _setAccountDatas = () => {
    if (accounts.kind === 'loaded' && userRoles !== null && userValidities !== null && accountInfos !== null) {
      let tempArr: AccountData[] = accounts.data.map(account => {
        let temp: AccountData = account;
        const tempRole = userRoles.find(x => x.uid === account.uid);
        const tempUserValidy = userValidities.find(x => x.uid === account.uid);
        const tempAccountInfo = accountInfos.find(x => x.uid === account.uid);
        if (tempRole !== undefined) {
          temp = { ...temp, role: tempRole.role } as AccountData;
        } else {
          temp = { ...temp, role: "unknown" as RoleType } as AccountData;
        }
        if (tempAccountInfo !== undefined) {
          if (tempAccountInfo.referralCode !== undefined) {
            temp = { ...temp, referralCode: tempAccountInfo.referralCode } as AccountData;
          }
          if (tempAccountInfo.phone !== undefined && account.phoneNumber === undefined) {
            temp = { ...temp, phoneNumber: tempAccountInfo.phone } as AccountData;
          }
        }
        if (tempUserValidy !== undefined) {
          if (tempUserValidy.valid_till !== null) {
            temp = { ...temp, is_valid: tempUserValidy.is_valid, valid_till: moment.utc(tempUserValidy.valid_till).local().format('YYYY-MM-DD') } as AccountData;
          } else {
            temp = { ...temp, is_valid: tempUserValidy.is_valid } as AccountData;
          }
        }
        return temp;
      })
      setAccountDatas(tempArr);
    }
  }

  const handleOpenEditUserDialog = (uid: string) => {
    setSelectedAccount(uid);
    setIsEditAccountDialogOpen(true);
  }

  const handleOpenResetPasswordDialog = (uid: string) => {
    setSelectedAccount(uid);
    setIsResetPasswordDialogOpen(true);
  }

  const handleOpenPlaytimeDialog = async (uid: string) => {
    setSelectedAccount(uid);
    setIsPlaytimeDialogOpen(true);
  }

  const handleEditDemoMissionDialog = (uid: string) => {
    setSelectedAccount(uid);
    setIsEditDemoMissionDialogOpen(true);
  }

  const closeEditAccountDialog = () => {
    setIsEditAccountDialogOpen(false);
    _setAccounts();
  }

  const closeResetPasswordDialog = () => {
    setIsResetPasswordDialogOpen(false);
  }

  const closePlaytimeDialog = () => {
    setIsPlaytimeDialogOpen(false);
  }

  const closeEditDemoMissionDialog = () => {
    setIsEditDemoMissionDialogOpen(false);
  }

  const closeAutoUpdateValidityDialog = () => {
    setIsAutoUpdateValidityDialogOpen(false);
  }

  const _isTrialAccount = (uid: string) => {
    if (accountDatas !== undefined && accounts.kind === "loaded") {
      const temp = accountDatas.filter(x => x.uid === uid)[0].role
      return (temp !== undefined && temp !== null)? isTrialAccount(temp) : false;
    }
  }

  const isCenterAccount = (uid: string) => {
    if (accountDatas !== undefined && accounts.kind === "loaded") {
      const temp = accountDatas.filter(x => x.uid === uid)[0].role;
      if (temp !== undefined && temp !== null && (temp === 'trial_center' || temp === 'center')) return true;
    }
    return false;
  }

  const handleAutoRenewValidity = async () => {
    // if (accounts !== undefined && accounts.kind === "loaded") {
    //   console.log(moment.utc().valueOf());
    //   accounts.data.forEach(async x => {
    //     console.log(x.valid_till);
    //     if (x.valid_till === undefined) {
    //       await updateUserValidity({
    //         uid: x.uid,
    //         valid_till: moment().add(7, "d").hour(23).minute(59).valueOf(),
    //         is_valid: true,
    //       });
    //     }
    //   })
    // }
    setIsAutoUpdateValidityDialogOpen(true);
  }

  const handleDeleteDialogOpen = (uid: string | undefined) => {
    if (uid !== undefined) {
      setSelectedAccount(uid);
      setIsDeleteDialogOpen(true);
    }
  }

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
  }

  const handleDeleteAccount = async (uid: string) => {
    try {
      const temp = await getUserProfile(uid);
      if (temp !== null) {
        await deleteUserProfile(uid);
      }
      await updateFirebaseAccountInfo({ uid: uid, disabled: true });
      setIsDeleteDialogOpen(false);
      setSnackBar({
        ...snackBar,
        open: true,
        message: t("msg.deleted_successfully"),
        variant: "success",
      });
      _setAccountDatas();
    } catch (e) {
      console.log(e);
    }
  }

  // Table States
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("UID");
  const [tableDatas, setTableDatas] = useState<AccountData[]>([]);
  const [tableTempDatas, setTableTempDatas] = useState<AccountData[]>([]);
  const [allProfiles, setAllProfiles] = useState<AccountData[]>([]);

  useEffect(() => {
    if (accountDatas && accounts.kind === "loaded") {
      setTableDatas(accountDatas);
      setTableTempDatas(accountDatas);
      setAllProfiles(accountDatas);
    }
  }, [accountDatas])

  useEffect(() => {
    handleDataFiltering();
  }, [allProfiles])

  // Table functions
  const handleChangePage = (e: unknown, newPage: number) => {
      setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(+e.target.value);
      setPage(0);
  };

  const handleRequestSort = (e: unknown, property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }

  // filtered: determine the element to show or not to show
  // roleValid: check user role whether the user have permission to view the specific element
  const [columns, setColumns] = useState<Column[]>([
    { id: "uid", label: "uid", minWidth: 500, filtered: false },
    { id: "email", label: "email", minWidth: 500, filtered: false },
    { id: "phoneNumber", label: "phone", minWidth: 500, filtered: true },
    { id: "displayName", label: "displayName", minWidth: 500, filtered: false },
    { id: "referralCode", label: "referralCode", minWidth: 500, filtered: true },
    { id: "emailVerified", label: "confirm_email", minWidth: 500, filtered: true },
    { id: "role", label: "role", minWidth: 500, filtered: false },
    { id: "is_valid", label: "is_valid", minWidth: 500, filtered: true },
    { id: "valid_till", label: "valid_till", minWidth: 500, filtered: false },
    { id: "lastSignInTime", label: "lastSignInTime", minWidth: 500, filtered: false },
    { id: "creationTime", label: "creationTime", minWidth: 500, filtered: false },
  ])
  const [allColumn, setAllColumn] = useState<boolean>(false);

  // Column Filter functions
  const [columnFilterOpen, setColumnFilterOpen] = useState<boolean>(false);
  const anchorColumnRef = useRef<any>(null);
  const prevColumnFilterOpen = useRef<boolean>(columnFilterOpen);
  
  const handleColumnFilterToggle = () => {
    setColumnFilterOpen((prevColumnFilterOpen: boolean) => !prevColumnFilterOpen);
  };

  const handleColumnFilterClose = (e: any) => {
    if (anchorColumnRef.current && anchorColumnRef.current.contains(e.target)) return;
    setColumnFilterOpen(false);
  }

  const handleColumnFilter = (id: string) => {
    if (id === "all") {
      setAllColumn(!allColumn);
      setColumns(
        columns.map( item => {
          if (item.id === "uid") { return item; }
          return {
            ...item,
            filtered: allColumn,
          }
        })
      )
    } else {
      if (allColumn) setAllColumn(false);
      setColumns(
        columns.map( item => {
          if (item.id !== id) { return item; }
          return {
            ...item,
            filtered: !item.filtered,
          }
        })
      )
    }
  }

  function handleListKeyDown(e: any) {
    if (e.key === "Tab") {
      e.preventDefault();
      setColumnFilterOpen(false);
      setDataFilterOpen(false);
    } else if (e.key === "Escape") {
      setColumnFilterOpen(false);
      setDataFilterOpen(false);
    }
  }

  useEffect(() => {
    if (prevColumnFilterOpen.current === true && columnFilterOpen === false) {
      anchorColumnRef.current.focus();
    }
    prevColumnFilterOpen.current = columnFilterOpen;
  }, [columnFilterOpen]);

  // Search Function
  function handleSearch(e: any) {
    let showProfiles: AccountData[] = [];

    if (e.target.value && e.target.value.replace(/\s/g, "").length) {
      let target: string = e.target.value.replace(/\s/g, "")
      tableTempDatas.filter(doc => 
        doc.displayName?.replace(/\s/g, "").includes(target) ||
        doc.email?.includes(target) ||
        doc.referralCode?.replace(/\s/g, "").includes(target) ||
        doc.role?.includes(target) ||
        doc.uid?.includes(target) || 
        doc.valid_till?.toString().includes(e.target.values) || 
        doc.lastSignInTime?.toString().includes(e.target.value) ||
        doc.creationTime?.toString().includes(e.target.value) ||
        doc.phoneNumber?.toString().includes(e.target.value) || 
        ((t("accountsPage.never_log_in").includes(e.target.value))? doc.lastSignInTime === "Invalid date" : false ) ||
        ((t("accountsPage.valid").includes(e.target.value))? doc.is_valid : false ) ||
        ((t("accountsPage.invalid").includes(e.target.value))? !doc.is_valid : false ) ||
        (("yes".includes(e.target.value.toLocaleLowerCase()))? doc.emailVerified : false ) ||
        (("no".includes(e.target.value.toLocaleLowerCase()))? !doc.emailVerified : false )
      ).forEach(doc => showProfiles.push(doc));
    } else {
      showProfiles = tableTempDatas;
    }

    setTableDatas(showProfiles);
    setPage(0);
  }

  // Data Filter functions
  const [dataFilterOpen, setDataFilterOpen] = useState<boolean>(false);
  const [dataFilterAttributes, setDataFilterAttributes] = useState<DataFilter[]>([
    { id: "referralCode", value: "all" },
    { id: "role", value: "all" },
    { id: "is_valid", value: "all" },
    { id: "valid_till", value: "all", boundary: ">" },
    { id: "lastSignInTime", value: "all", boundary: ">" },
    { id: "creationTime", value: "all", boundary: ">" },
  ]);
  const [isAll, setIsAll] = useState<boolean>(true);
  const anchorDataRef = useRef<any>(null);
  const prevDataFilterOpen = useRef<boolean>(dataFilterOpen);

  const dataAttributes: DataAttributes[] = [
    { id: "referralCode", label: "referralCode", values: ["all", "with_ref_code", "without_ref_code"] },
    { id: "role", label: "role", values: ["all", "admin", "trial_center", "center", "trial_center_user", "center_user", "trial_individual", "individual", "unknown"] },
    { id: "is_valid", label: "label_valid?", values: ["all", "valid", "invalid"] },
    { id: "valid_till", label: "label_valid_til", 
      values: ["all", `5y`, `3y`, `1y`, `6m`, `3m`, "accountsPage.expired"], 
      boundary: [
        { id: "gt", label: "after", values: ">" },
        { id: "lt", label: "within", values: "<"},
      ]
    },
    { id: "lastSignInTime", label: "lastSignInTime", values: ["all", `5y`, `3y`, `1y`, `6m`, `3m`, "date_noun.today", "accountsPage.never_log_in"],
      boundary: [
        { id: "gt", label: "before", values: ">" },
        { id: "lt", label: "within", values: "<"},
      ]
    },
    { id: "creationTime", label: "creationTime", values: ["all", `5y`, `3y`, `1y`, `6m`, `3m`],
      boundary: [
        { id: "gt", label: "before", values: ">" },
        { id: "lt", label: "within", values: "<"},
      ]
    },
  ]

  function handleDataFilterToggle() {
    setDataFilterOpen((prevDataFilterOpen: boolean) => !prevDataFilterOpen);
  }

  const handleDataFilterClose = (e: any) => {
    if (anchorDataRef.current && (anchorDataRef.current.contains(e.target))) return;
    setDataFilterOpen(false);
  }

  const handleDataFilter = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    setDataFilterAttributes(dataFilterAttributes.map(item => {
      return (id !== item.id)? { ...item } : { ...item, value: e.target.value };
    }))
  }

  const handleDataInequality = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    setDataFilterAttributes(dataFilterAttributes.map(item => {
      return (id !== item.id)? { ...item } : { ...item, boundary: e.target.value };
    }))
  }

  const handleDataFiltering = () => {
    let showProfiles: AccountData[] = allProfiles;

    setIsAll(dataFilterAttributes.every(item => item.value === "all"));
    
    dataFilterAttributes.forEach(item => {
      if (item.value !== "all") {
        let date = new Date();
        let month = date.getMonth() + 1;
        let stringMonth = ((month/10) < 1? "0" : "") + month;
        let day = (date.getDate()/10) < 1? "0"+date.getDate() : date.getDate();
        let today = date.getFullYear()+"-"+stringMonth+"-"+day;
        let periodVal: number = parseInt(item.value, 10);
        let isYear = item.value.replace("y", "").length === 1;
        let isMonthUnderFlow = month < periodVal;

        if (item.id === "referralCode") {
          showProfiles = showProfiles.filter(doc => item.value === "with_ref_code"? doc.referralCode : !doc.referralCode);
        }

        if (item.id === "role") {
          showProfiles = showProfiles.filter(doc => doc.role === item.value);
        }

        if (item.id === "is_valid") {
          showProfiles = showProfiles.filter(doc => item.value === "valid"? doc.is_valid : !doc.is_valid);
        }

        if (item.id === "valid_till") {
          if (item.value === "accountsPage.expired") {
            showProfiles = showProfiles.filter(doc => doc.valid_till? doc.valid_till < today: false);
          } else {
            let periodVal: number = parseInt(item.value, 10) * ((item.value.includes("m"))? 1 : 12) ;
            month = (month + periodVal);
            stringMonth = ((month % 12 /10) < 1? "0" : "")+(month % 12);
            let year = date.getFullYear() + Math.floor(month / 12);
            let targetDate = year+"-"+stringMonth+"-"+day;
            // let targetDate = day+"/"+stringMonth+"/"+year;
            showProfiles = showProfiles.filter(doc => doc.valid_till? (item.boundary === ">")? doc.valid_till > targetDate : (doc.valid_till < targetDate && doc.valid_till > today) : false);
          }
        }

        if (item.id === "lastSignInTime") {
          if (item.value === "accountsPage.never_log_in") {
            showProfiles = showProfiles.filter(doc => doc.lastSignInTime === "Invalid date");
          } else if (item.value === "date_noun.today") {
            showProfiles = showProfiles.filter(doc => doc.lastSignInTime? doc.lastSignInTime === today && doc.lastSignInTime !== "Invalid date" : false);
          } else {
            month = (isYear)? month : (isMonthUnderFlow)? (12 + month - periodVal) : (month - periodVal);
            let year = (isYear)? date.getFullYear() - periodVal : (isMonthUnderFlow)? (date.getFullYear() - 1) : date.getFullYear();
            let stringMonth = ((month/10) < 1? "0" : "") + month;
            let targetDate = year+"-"+stringMonth+"-"+day;
            // let targetDate = day+"/"+stringMonth+"/"+year;
            let isWithin = item.boundary === "<";
            showProfiles = showProfiles.filter(doc => doc.lastSignInTime? (isWithin)? (targetDate < doc.lastSignInTime && doc.lastSignInTime !== "Invalid date") : (targetDate >= doc.lastSignInTime && doc.lastSignInTime !== "Invalid date") : false);
          }
        }

        if (item.id === "creationTime") {
          month = (isYear)? month : (isMonthUnderFlow)? (12 + month - periodVal) : (month - periodVal);
          let year = (isYear)? date.getFullYear() - periodVal : (isMonthUnderFlow)? (date.getFullYear() - 1) : date.getFullYear();
          let stringMonth = ((month/10) < 1? "0" : "") + month;
          let targetDate = year+"-"+stringMonth+"-"+day;
          // let targetDate = day+"/"+stringMonth+"/"+year;
          let isWithin = item.boundary === "<";
          showProfiles = showProfiles.filter(doc => doc.creationTime? (isWithin)? targetDate < doc.creationTime : targetDate > doc.creationTime : false);
        }
      }
    })
    setTableDatas(showProfiles);
    setTableTempDatas(showProfiles);
    setPage(0);
  }

  useEffect(() => {
    handleDataFiltering();
  }, [dataFilterAttributes])

  useEffect(() => {
    if (prevDataFilterOpen.current === true && dataFilterOpen === false) {
      anchorDataRef.current.focus();
    }
    prevDataFilterOpen.current = dataFilterOpen;
  }, [dataFilterOpen]);

  return (
    <div>
      <StyledContainer maxWidth="lg" style={{ paddingTop: 90, paddingBottom: 80, maxWidth: "100%", maxHeight: "80%" }}>
        {loggedInUser && loggedUserRole === "admin" ?
          <>
            <Toolbar
              sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                justifyContent: "space-between",
              }}
            >
              <div>
                <Button href="/newaccount" color="primary" variant="contained" style={{ marginRight: 20, marginBottom: 5 }}>
                  {t("accountsPage.createNewAccount")}
                </Button>
                <Button onClick={handleAutoRenewValidity} color="primary" variant="contained" style={{ marginBottom: 5 }} >
                  {t("accountsPage.extendAccountValidTil")}
                </Button>
              </div>
              <div style={{ display: "inline-flex", justifyContent: "space-between" }}>
                <Tooltip title={t("table_components.search")}>
                  <Search sx={{ zIndex: 3 }}>
                    <StyledIconWrapper>
                      <SearchIcon/>
                    </StyledIconWrapper>
                    <StyledInputBase
                      inputProps={{ "aria-label": "search" }}
                      onChange={(e) => handleSearch(e)}
                    />
                  </Search>
                </Tooltip>
                <Divider sx={{ height: "auto", m: 1 }} orientation="vertical" />
                <Tooltip title={t("table_components.columnFilter")}>
                  <IconButton
                    ref={anchorColumnRef}
                    id="columnFilterButt"
                    aria-controls={columnFilterOpen ? "column-composition-menu" : undefined}
                    aria-expanded={columnFilterOpen ? "true" : undefined}
                    aria-haspopup="true"
                    onClick={handleColumnFilterToggle}
                  >
                    <AlignHorizontalLeftIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  key="column-filter-menu"
                  id="column-composition-menu"
                  anchorEl={anchorColumnRef.current}
                  onKeyDown={handleListKeyDown}
                  open={columnFilterOpen}
                  onClose={handleColumnFilterClose}
                >
                  <MenuList
                    id="column-composition-menu"
                    aria-labelledby="composition-button"
                    onKeyDown={handleListKeyDown}
                  >
                    <MenuItem key={`column-filter-all`} sx={{ padding: 0, paddingInline: 1, width: "100%", zIndex: 2 }} onClick={() => handleColumnFilter("all")}>
                      <Checkbox value="all" checked={allColumn}/>
                      {t("common.all")}
                    </MenuItem>
                    {
                      columns.map((col) =>
                        (col.id !== "uid" && (
                          <MenuItem key={`column-filter-${col.label}`} sx={{ padding: 0, paddingInline: 1, width: "100%", zIndex: 2 }} onClick={() => handleColumnFilter(col.id)}>
                            <Checkbox
                              value={col.id}
                              checked={!col.filtered}
                            />
                            {t(`common.${col.label}` as unknown as TemplateStringsArray)}
                          </MenuItem>
                      )))
                    }
                  </MenuList>
                </Menu>
                <Tooltip title={t("table_components.dataFilter")}>
                  <IconButton
                    ref={anchorDataRef}
                    id="dataFilterButt"
                    aria-controls={dataFilterOpen ? "data-composition-menu" : undefined}
                    aria-expanded={dataFilterOpen ? "true" : undefined}
                    aria-haspopup="true"
                    onClick={handleDataFilterToggle}
                    color={!isAll? "warning" : "default"}
                  >
                    <FilterListIcon/>
                  </IconButton>
                </Tooltip>
                <Menu
                  key={`data-filter-menu`}
                  id="data-composition-menu"
                  anchorEl={anchorDataRef.current}
                  onKeyDown={handleListKeyDown}
                  open={dataFilterOpen}
                  onClose={handleDataFilterClose}
                >
                    {
                      dataAttributes.map((item) => {
                        if (item.id === "role") {
                          return (
                            <TextField
                              select
                              label={t(`common.${item.label}` as unknown as TemplateStringsArray)}
                              value={dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDataFilter(e, item.id)}
                              variant="standard"
                              key={`data-filter-${item.label}`}
                              sx={{ display: "flex", margin: 1, width: "auto" }}
                            >
                              {
                                item.values.map(item_val => (
                                  <MenuItem key={`data-filter-${item.label}-value=${item_val}`} value={item_val}>
                                    {item_val === "all"? t("accountsPage.all") : item_val}
                                  </MenuItem>
                                ))
                              }
                            </TextField>
                          )
                        }

                        if (item.id === "valid_till" || item.id === "lastSignInTime" || item.id === "creationTime") {
                          let isShowAll = dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value === "all";
                          let isOutDate = dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value === "accountsPage.expired";
                          let isNeverLoggedIn = dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value === "accountsPage.never_log_in";
                          let isToday = dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value === "date_noun.today";

                          return (
                            <div 
                              style={{ width: 250, display: "flex" }} 
                              key={`data-filter-${item.label}`}
                            >
                              <TextField
                                select
                                label={t(`common.${item.label}` as unknown as TemplateStringsArray)}
                                value={dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDataFilter(e, item.id)}
                                variant="standard"
                                key={`data-filter-${item.label}-value`}
                                sx={{ display: "inline-flex", margin: 1, width: (isShowAll || isOutDate || isNeverLoggedIn || isToday)? "100%" : "50%" }}
                              >
                                {
                                  item.values.map(item_val => (
                                    <MenuItem key={`data-filter-${item.label}-value=${item_val}`} value={item_val}>
                                      {
                                        item_val === "all" ?
                                          t("accountsPage.all")
                                        : item_val.replace("y", "").length === 1?
                                            item_val.replace("y", "") + t("units_date.year")
                                          : item_val.replace("m", "").length === 1?
                                              item_val.replace("m", "") + t("units_date.piece") + t("units_date.month")
                                            : t(`${item_val}` as unknown as TemplateStringsArray)
                                      }
                                    </MenuItem>
                                  ))
                                }
                              </TextField>
                              {
                                (!isShowAll && !isOutDate && !isNeverLoggedIn && !isToday) && (
                                  <TextField
                                    select
                                    label=" "
                                    value={dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.boundary}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDataInequality(e, item.id)}
                                    variant="standard"
                                    key={`data-filter-${item.label}-inequality`}
                                    sx={{ display: "inline-flex", margin: 1, width: "50%" }}
                                  >
                                    {
                                      item.boundary?.map(sign => (
                                        <MenuItem key={`data-filter-${item.label}-inequality-${sign.id}`} sx={{ padding: 1 }} value={sign.values}>
                                          {t(`accountsPage.${sign.label}` as unknown as TemplateStringsArray)}
                                        </MenuItem>
                                      ))
                                    }
                                  </TextField>
                                )
                              }
                            </div>
                          )
                        } 
                        return (
                          <TextField
                            select
                            label={t(`common.${item.label}` as unknown as TemplateStringsArray)}
                            value={dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDataFilter(e, item.id)}
                            variant="standard"
                            key={`data-filter-${item.label}`}
                            sx={{ display: "flex", margin: 1, width: "auto" }}
                          >
                            {
                              item.values.map(item_val => (
                                <MenuItem key={`data-filter-${item.label}-value=${item_val}`} value={item_val}>
                                  {t(`accountsPage.${item_val}` as unknown as TemplateStringsArray)}
                                </MenuItem>
                              ))
                            }
                          </TextField>
                        )
                      })
                    }
                </Menu>
              </div>
            </Toolbar>
            {((accounts !== undefined && accounts.kind === "loading") || accountDatas === undefined) && <StyledCircularProgress />}
            {accountDatas !== undefined && accounts.kind === "loaded" &&
              <Paper sx={{ width: "100%", zIndex: -1 }}>
                <TableContainer sx={{ maxHeight: "60vh" }}>
                  <Table stickyHeader aria-label="sticky table" sx={{ maxHeight: "80vh"}}>
                    <CustomHead
                      order={order}
                      orderBy={orderBy}
                      columns={columns}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      <TableRow />
                      {
                        tableDatas
                          .sort(getComparator(order, orderBy))
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map(thisUserProfile => {
                            return (
                              <CustomRow
                                key={`user-${thisUserProfile.uid}`}
                                userProfile={thisUserProfile}
                                columns={columns}
                                _isTrialAccount={_isTrialAccount}
                                handleOpenEditUserDialog={handleOpenEditUserDialog}
                                handleOpenResetPasswordDialog={handleOpenResetPasswordDialog}
                                handleOpenPlaytimeDialog={handleOpenPlaytimeDialog}
                                handleEditDemoMissionDialog={handleEditDemoMissionDialog}
                                handleDeleteDialogOpen={handleDeleteDialogOpen}
                              />
                            )
                          })
                      }
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 30, 50, 100]}
                  component="div"
                  count={tableDatas.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("common.rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} ${t("common.of")} ${count !== -1 ? count : `${t("common.more_than")}${to}}`}`
                  }
                />
              </Paper>
            }
            {
              selectedAccount !== undefined &&
              <EditAccountDialog isOpen={isEditAccountDialogOpen} onClose={closeEditAccountDialog} uid={selectedAccount} />
            }
            {
              selectedAccount !== undefined && snackBar !== undefined &&
              <ResetPasswordDialog isOpen={isResetPasswordDialogOpen} onClose={closeResetPasswordDialog} uid={selectedAccount} snackBar={snackBar} setSnackbar={setSnackBar} />
            }
            {selectedAccount !== undefined &&
              <PlaytimeDialog isOpen={isPlaytimeDialogOpen} onClose={closePlaytimeDialog} isCenter={isCenterAccount(selectedAccount)} uid={selectedAccount} />
            }
            {
              (selectedAccount !== undefined && missionMetadata.domainMission) &&
              <EditDemoMissionDialog isOpen={isEditDemoMissionDialogOpen} onClose={closeEditDemoMissionDialog} uid={selectedAccount} />
            }
            <AutoUpdateValidityDialog isOpen={isAutoUpdateValidityDialogOpen} onClose={closeAutoUpdateValidityDialog} />
            {selectedAccount !== undefined && accountDatas !== undefined &&
              <DeleteDialog open={isDeleteDialogOpen} onClose={handleDeleteDialogClose} type="acc" selectedName={accountDatas.filter(x => x.uid === selectedAccount)[0].displayName} selectedId={selectedAccount} handleDelete={handleDeleteAccount} />}
            <SimpleSnackBar open={snackBar.open} onClose={closeErrorSnackBar} message={snackBar.message} variant={snackBar.variant} />
          </> :
          <SimpleSnackBar open={true} onClose={closeErrorSnackBar} message={t("msg.no_permission_to_view")} variant="warning" autoClose={99999} />
        }
      </StyledContainer >
    </div >
  );
}

//// Custom Table ////
const StyledTableCell = styled(TableCell)({
  padding: 14,
  width: "auto",
})

interface Column {
  id:
    "uid" | 
    "email" | 
    "phoneNumber" | 
    "displayName" | 
    "referralCode" | 
    "emailVerified" |
    "role" |
    "is_valid" |
    "valid_till" | 
    "lastSignInTime" | 
    "creationTime";
  label: string;
  filtered?: boolean;
  minWidth?: number;
  align?: "left";
  format?: (value: number) => string;
}

interface DataAttributes {
  id:
    "referralCode" | 
    "role" |
    "is_valid" |
    "valid_till" | 
    "lastSignInTime" | 
    "creationTime";
  label: string;
  key?: string;
  values: string[];
  boundary?: {
    id: 
      "gt" |
      "lt";
    label: string;
    values: 
      ">" |
      "<";
  }[];
}

interface DataFilter {
  id: 
    "uid" | 
    "email" | 
    "phoneNumber" | 
    "displayName" | 
    "referralCode" | 
    "emailVerified" |
    "role" |
    "is_valid" |
    "valid_till" | 
    "lastSignInTime" | 
    "creationTime";
  value: string;
  boundary?: string;
}

function Comparator(a: any, b: any, orderBy: string) {
  return (b[orderBy] < a[orderBy] || !b[orderBy])? -1 : (b[orderBy] > a[orderBy] || !a[orderBy])? 1 : 0;
}

function getComparator(order: "asc" | "desc", orderBy: string) {
  return order === "desc"
    ? (a: any, b: any) => Comparator(a, b, orderBy)
    : (a: any, b: any) => -Comparator(a, b, orderBy);
}

function CustomRow(
  props: {
    columns: Column[],
    userProfile: AccountData,
    _isTrialAccount: (uid: string) => boolean | undefined,
    handleOpenEditUserDialog: (uid: string) => void,
    handleOpenResetPasswordDialog: (uid: string) => void,
    handleOpenPlaytimeDialog: (uid: string) => void,
    handleEditDemoMissionDialog: (uid: string) => void,
    handleDeleteDialogOpen: (uid: string) => void,
  }
) {
  const {t} = useTranslation();
  const { 
    columns, 
    userProfile,
    _isTrialAccount,
    handleOpenEditUserDialog, 
    handleOpenResetPasswordDialog, 
    handleOpenPlaytimeDialog, 
    handleEditDemoMissionDialog, 
    handleDeleteDialogOpen 
  } = props;
  const user_uid = userProfile.uid;
  const user_key = `user-${user_uid}`;
  const [open, setOpen] = useState(false);

  const handleRowHover = (e: any) => {
    if (e.target.id === user_uid) { setOpen(true); }
  }

  const handleRowLeave = (e: any) => {
    if (e.target.id === user_uid) { setOpen(false); }
  }

  return (
    <>
    <TableRow onMouseEnter={(e) => handleRowHover(e)} onMouseLeave={(e) => handleRowLeave(e)} hover sx={{ "& > *": {borderBottom: "unset" }}}>
      <StyledTableCell 
        key={`${user_key}-uid-cell`}
        id={user_uid}
        component="th" 
        scope="row"
      >
        {user_uid}
      </StyledTableCell>
      {
        columns.map(col => {
          if (col.filtered) {
            return;
          }
          if (col.id === "is_valid") {
            return (
              <StyledTableCell id={user_uid} key={`${user_key}-${col.id}-cell`} align="left">
                {(userProfile.is_valid)? t("accountsPage.valid") : t("accountsPage.invalid")}
              </StyledTableCell>
            )
          }
          if (col.id === "lastSignInTime") {
            return (
              <StyledTableCell id={user_uid} key={`${user_key}-${col.id}-cell`} align="left">
                {(userProfile.lastSignInTime === "Invalid date")? t("accountsPage.never_log_in") : moment(userProfile.lastSignInTime).format("YYYY-MM-DD HH:mm:ss")}
              </StyledTableCell>
            )
          }
          if (col.id === "emailVerified") {
            return (
              <StyledTableCell id={user_uid} key={`${user_key}-${col.id}-cell`} align="left">
                {userProfile.emailVerified? "Yes" : "No"}
              </StyledTableCell>
            )
          }
          if (col.id === "creationTime") {
            return (
              <StyledTableCell id={user_uid} key={`${user_key}-${col.id}-cell`} align="left">
                {moment(userProfile[col.id]).format("YYYY-MM-DD HH:mm:ss")}
              </StyledTableCell>
            )
          }
          if (col.id === "valid_till") {
            return (
              <StyledTableCell id={user_uid} key={`${user_key}-${col.id}-cell`} align="left">
                {moment(userProfile[col.id]).format("YYYY-MM-DD")}
              </StyledTableCell>
            )
          }
          if (col.id !== "uid") {
            return (
              <StyledTableCell id={user_uid} key={`${user_key}-${col.id}-cell`} align="left">
                {userProfile[col.id]}
              </StyledTableCell>
            )
          }
        })
      }       
    </TableRow>   
    <TableRow onMouseEnter={(e) => handleRowHover(e)} onMouseLeave={(e) => handleRowLeave(e)} sx={{ backgroundColor: "#D3D3D3", width: "auto" }}>
      <TableCell sx={{ padding: 0 }} colSpan={16}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <div id={user_uid} style={{ padding: 5 }}>
            <Tooltip title={t("accountsPage.updateAccountInfo")}>
              <IconButton
                onClick={() => handleOpenEditUserDialog(user_uid)}
                color="inherit"
              >
                <EditIcon color='primary' />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("accountsPage.resetPassword")}>
              <IconButton
                onClick={() => handleOpenResetPasswordDialog(user_uid)}
                color="inherit"
              >
                <VpnKeyIcon color='primary' />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("accountsPage.viewAllUsersGameTime")}>
              <IconButton
                onClick={() => handleOpenPlaytimeDialog(user_uid)}
                color="inherit"
              >
                <AccessAlarmIcon color='primary' />
              </IconButton>
            </Tooltip>
            {
              _isTrialAccount(user_uid) === true &&
              <Tooltip title={t("accountsPage.updateAccountsTrialContent")}>
                <IconButton
                  onClick={() => handleEditDemoMissionDialog(user_uid)}
                  color="inherit"
                >
                  <SportsEsportsIcon color='primary' />
                </IconButton>
              </Tooltip>
            }
            <Tooltip title={t("accountsPage.deleteAccount")}>
              <IconButton
                onClick={() => handleDeleteDialogOpen(user_uid)}
                color="inherit"
              >
                <DeleteIcon color='primary' />
              </IconButton>
            </Tooltip>
          </div>
        </Collapse>
      </TableCell>
    </TableRow>
    </>
  )
}

function CustomHead(
  props: {
    order: "asc" | "desc" ,
    orderBy: string,
    columns: Column[],
    onRequestSort: (arg0: any, arg1: string) => void,
  }
) {
  const {t} = useTranslation();
  const { order, orderBy, columns, onRequestSort } = props;
  const createSortHandler = (property: string) => (e: any) => {
    onRequestSort(e, property);
  };

  return (
    <TableHead>
      <TableRow>
        {
          columns.map((col) => {
            return (
              (!col.filtered && (
                <StyledTableCell
                  key={col.id}
                  align={col?.align?? "left"}
                  // style={{ minWidth: col.minWidth }}
                  style={{ width: "auto" }}
                  sortDirection={orderBy === col.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : "asc"}
                    onClick={createSortHandler(col.id)}
                  >
                    {t(`common.${col.label}` as unknown as TemplateStringsArray)}
                    {
                      orderBy === col.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      )
                      : null
                    }
                  </TableSortLabel>
                </StyledTableCell>
            )))
          })
        }
      </TableRow>
    </TableHead>
  )
}

const Search = styled("div")(({theme}) => ({
  position: "relative",
  marginLeft: 0,
  marginRight: 7,
  width: "100%",
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}))

const StyledIconWrapper = styled("div")(({theme}) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}))

const StyledInputBase = styled(InputBase)(({theme}) => ({
  "& .MuiInputBase-input": {
    padding: theme.spacing(1,1,1,0),
    borderRadius: "25px",
    paddingLeft: "3em",
    paddingRight: "0",
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up('sm')]: {
      width: 0,
      '&:hover': {
        width: '20ch',
        backgroundColor: alpha("#BBBBBB", 0.25),
      },
      '&:focus': {
        width: '20ch',
        backgroundColor: alpha("#BBBBBB", 0.25),
      },
    },
  }
}))

// Original Data Table
//   <MUIDataTable
//     title={""}
//     data={accountDatas}
//     columns={[
//       {
//         label: ' ',
//         name: 'uid',
//         options: {
//           filter: false,
//           sort: false,
//           viewColumns: false,
//           customBodyRenderLite: (dataIndex) => (
//             <>
//               <Tooltip title="更改帳戶資料">
//                 <IconButton
//                   onClick={() => handleOpenEditUserDialog(accountDatas[dataIndex].uid)}
//                   color="inherit"
//                 >
//                   <EditIcon color='primary' />
//                 </IconButton>
//               </Tooltip>
//               <Tooltip title="重設密碼">
//                 <IconButton
//                   onClick={() => handleOpenResetPasswordDialog(accountDatas[dataIndex].uid)}
//                   color="inherit"
//                 >
//                   <VpnKeyIcon color='primary' />
//                 </IconButton>
//               </Tooltip>
//               <Tooltip title="檢視所有用家遊玩時間">
//                 <IconButton
//                   onClick={() => handleOpenPlaytimeDialog(accountDatas[dataIndex].uid)}
//                   color="inherit"
//                 >
//                   <AccessAlarmIcon color='primary' />
//                 </IconButton>
//               </Tooltip>
//               {_isTrialAccount(accountDatas[dataIndex].uid) === true &&
//                 <Tooltip title="更改帳戶試玩內容">
//                   <IconButton
//                     onClick={() => handleEditDemoMissionDialog(accountDatas[dataIndex].uid)}
//                     color="inherit"
//                   >
//                     <SportsEsportsIcon color='primary' />
//                   </IconButton>
//                 </Tooltip>}
//               <Tooltip title="刪除帳戶">
//                 <IconButton
//                   onClick={() => handleDeleteDialogOpen(accountDatas[dataIndex].uid)}
//                   color="inherit"
//                 >
//                   <DeleteIcon color='primary' />
//                 </IconButton>
//               </Tooltip>
//             </>
//           )
//         }
//       },
//       {
//         label: 'UID',
//         name: 'uid',
//         options: {
//           filter: true,
//           sort: true,
//           display: "false"
//         }
//       },
//       {
//         label: '電郵',
//         name: 'email',
//         options: {
//           filter: true,
//           sort: true,
//         }
//       },
//       {
//         label: '電話',
//         name: 'phoneNumber',
//         options: {
//           filter: true,
//           sort: true,
//           display: "false"
//         }
//       },
//       {
//         label: '顯示名稱',
//         name: 'displayName',
//         options: {
//           filter: true,
//           sort: true,
//         }
//       },
//       {
//         label: '推薦碼',
//         name: 'referralCode',
//         options: {
//           filter: true,
//           sort: true,
//           display: "false"
//         }
//       },
//       {
//         label: '確認電郵',
//         name: 'emailVerified',
//         options: {
//           filter: true,
//           sort: true,
//           display: "false",
//           customBodyRender: (value) => value ? "Yes" : "No"
//         }
//       },
//       {
//         label: '身份',
//         name: 'role',
//         options: {
//           filter: true,
//           sort: true,
//         }
//       },
//       {
//         label: '有效？',
//         name: 'is_valid',
//         options: {
//           filter: true,
//           sort: true,
//           display: "false",
//           customBodyRender: (value) => value ? "Yes" : "No"
//         }
//       },
//       {
//         label: '有效期至',
//         name: 'valid_till',
//         options: {
//           filter: true,
//           sort: true,
//         }
//       },
//       {
//         label: '最後登入',
//         name: 'lastSignInTime',
//         options: {
//           filter: false,
//           sort: true,
//           customBodyRender: (value) => value === "Invalid date" ? "從未登入" : value
//         }
//       },
//       {
//         label: '建立日期',
//         name: 'creationTime',
//         options: {
//           filter: false,
//           sort: true,
//         }
//       },

//     ]}
//     options={{ selectableRows: 'none', elevation: 0, print: false, download: false }} />
// }